import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { InView } from 'react-intersection-observer'

import { routes } from 'constants/routes'

import AspectRatioGrid from 'components/AspectRatioGrid'
import Breakpoint from 'components/Breakpoint'
import Table from 'components/Table'

import useUserProfile from 'modules/user/useUserProfile'

import {
  ShareListItemFragment,
  VersionFragment,
  useGetDocumentListSettingsQuery,
  ProjectFragment,
} from 'gqlTypes'

import { ResponsiveValues } from 'styles/breakpoints'

import LoadingState from '../LoadingState'
import ShareListItemDrag from '../ShareListItemDrag'

import ShareDropdownItems from '../ShareActionsDropdown'

import { getDownloadProps, DownloadProps } from '../../utils'

import ConnectedDocumentItem from './ConnectedDocumentItem'
import {
  DocumentGridItemPlaceholder,
  DocumentTableItemPlaceholder,
} from '../DocumentItem'

import {
  ActionTableCell,
  Footer,
  TableWrapper,
  FocusLink,
  HeaderTableCell,
} from './DocumentItemsLayout.styles'

type DropdownMenu =
  | 'document'
  | ((share: ShareListItemFragment, download: DownloadProps) => React.ReactNode)

type Items = ShareListItemFragment | ProjectFragment

interface DocumentItemsLayoutProps {
  shares: Items[]
  loading: boolean
  showProjectName: boolean
  onLoadMore: () => Promise<any>
  dropdownMenu: DropdownMenu
  totalShareCount: number
  action?: 'link' | ((share: Items) => void)
  header?: React.ReactNode
}

export const TABLE_HEADER = [
  { label: 'Document', customCell: HeaderTableCell },
  { label: 'Project', customCell: HeaderTableCell },
  { label: 'Last Updated', customCell: HeaderTableCell },
  { label: 'Updated By', customCell: HeaderTableCell },
  { label: 'Action', customCell: ActionTableCell },
]

const COLUMNS: ResponsiveValues<number> = [2, 2, 3, 3, 3, 4, 5, 6]
const GUTTER: ResponsiveValues<number> = [16, 16, 16, 16, 40, 40, 40]
const VERTICAL_SPACE: ResponsiveValues<number> = [24, 24, 24, 24, 40, 40, 40]
const PAGE_SIZE = 20

// Utils
const renderDropdownMenu = (
  share: ShareListItemFragment,
  dropdownMenu: DropdownMenu
) => {
  const version = share?.version as VersionFragment
  const download = getDownloadProps(version)

  if (dropdownMenu === 'document') {
    return (
      <ShareDropdownItems
        share={share}
        version={{
          currentVersion: version,
          latestVersion: version,
        }}
        download={download}
        isViewingLatestVersion={true}
      />
    )
  }

  return dropdownMenu(share, download)
}

const buildPlaceholderArray = (number: number) => {
  if (number <= 0) {
    return []
  }

  if (number > PAGE_SIZE) {
    return ['listener', ...Array(PAGE_SIZE - 1)] as const
  }

  return ['listener', ...Array(number - 1)] as const
}

// Auxiliary components
const GridLayout: React.FC = ({ children }) => (
  <AspectRatioGrid
    columns={COLUMNS}
    verticalRowSpace={VERTICAL_SPACE}
    gutterSize={GUTTER}>
    {children}
  </AspectRatioGrid>
)

const DocumentItemsLayout: React.FC<DocumentItemsLayoutProps> = props => {
  const {
    shares,
    showProjectName,
    onLoadMore,
    loading,
    dropdownMenu,
    action = 'link',
    totalShareCount,
    header,
  } = props

  const history = useHistory()

  // Make sure the onLoadMore Reference is always the latest
  const onLoadMoreRef = useRef<() => Promise<any>>(onLoadMore)
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  // Get the User Identifier to show the "You" label
  const { data: userData } = useUserProfile()
  const userIdentifier = userData?.me.identifier

  if (loading && shares.length === 0) {
    return <LoadingState />
  }

  const renderGridItem = (share: ShareListItemFragment, isMobile: boolean) => {
    if (action !== 'link') {
      return (
        <ShareListItemDrag
          key={share.identifier}
          shareId={share.identifier}
          disabled={
            isMobile || !(share.userIsOwner || share.userCanUpdateSettings)
          }>
          <ConnectedDocumentItem
            key={share.identifier}
            share={share}
            presentation="grid"
            showProjectName={showProjectName}
            renderDropdownItems={() => renderDropdownMenu(share, dropdownMenu)}
            onClick={() => action(share)}
          />
        </ShareListItemDrag>
      )
    }

    return (
      <ShareListItemDrag
        key={share.identifier}
        shareId={share.identifier}
        disabled={
          isMobile || !(share.userIsOwner || share.userCanUpdateSettings)
        }>
        <FocusLink
          draggable={false}
          to={routes.SHARE_VIEW.create({ shareID: share.identifier })}>
          <ConnectedDocumentItem
            key={share.identifier}
            share={share}
            presentation="grid"
            showProjectName={showProjectName}
            renderDropdownItems={() => renderDropdownMenu(share, dropdownMenu)}
            userIdentifier={userIdentifier}
          />
        </FocusLink>
      </ShareListItemDrag>
    )
  }

  const renderTableItem = (share: ShareListItemFragment) => {
    return (
      <ConnectedDocumentItem
        share={share}
        presentation="list"
        showProjectName={showProjectName}
        onClick={event => {
          if (action !== 'link') {
            action(share)
            return
          }

          const link = routes.SHARE_VIEW.create({
            shareID: share.identifier,
          })

          if (event.metaKey) {
            /* Simulate the link opening with meta key */
            window.open(link, '_blank')
          } else {
            history.push(link)
          }
        }}
        renderDropdownItems={() => renderDropdownMenu(share, dropdownMenu)}
        userIdentifier={userIdentifier}
      />
    )
  }

  const renderLayout = (isMobile: boolean) => {
    const placeholders = buildPlaceholderArray(
      (totalShareCount || 0) - shares.length
    )

    // Merge the items with the placeholders have them rendered together
    // This prevents the page from bumping when the placeholders are replaced
    // with actual items
    const sharesAndPlaceholders = [...shares, ...placeholders]

    // Create the InView listener warn when it starts showing the placeholders, this will load the next page
    const inViewListener =
      placeholders.length > 0 ? (
        <InView onChange={inView => inView && onLoadMoreRef.current?.()}>
          <span className="sr-only">Loading Placeholder</span>
        </InView>
      ) : null

    if (documentsLayout === 'LIST') {
      return (
        <TableWrapper $hideProjects={!showProjectName}>
          <Table
            header={TABLE_HEADER}
            items={sharesAndPlaceholders}
            renderItemKey={share =>
              // if it's not a share the table will generate a index based key
              typeof share === 'object' ? share.identifier : undefined
            }
            renderItem={item => {
              if (typeof item === 'object') {
                return renderTableItem(item)
              }

              return (
                <DocumentTableItemPlaceholder
                  inViewListener={item === 'listener' && inViewListener}
                />
              )
            }}
            evenColumns={!isMobile}
          />
        </TableWrapper>
      )
    } else {
      return (
        <GridLayout>
          {sharesAndPlaceholders.map((item, index) => {
            if (typeof item === 'object') {
              return renderGridItem(item, isMobile)
            }

            return (
              <DocumentGridItemPlaceholder
                key={index}
                inViewListener={item === 'listener' && inViewListener}
              />
            )
          })}
        </GridLayout>
      )
    }
  }

  return (
    <>
      {header}
      <Breakpoint on="sm">{matches => renderLayout(!matches)}</Breakpoint>
      <Footer />
    </>
  )
}

export default DocumentItemsLayout
